import { G } from '@mobily/ts-belt'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { useDietsStore } from '@/store/dietsStore'
import { useGlobalStore } from '@/store/globalStore'

import type { Nutrients } from '@/utils/nutrients'

export const usePatientNorms = () => {
  const dietStore = useDietsStore()
  const { diet } = storeToRefs(dietStore)
  const { hasPerm, showAppUpgradePlanModal } = useGlobalStore()
  const bind = <T>(get: () => T, set: (value: T) => void) => computed({ get, set })
  const patientBirthdate = computed<string, Date>({
    get: () => diet.value?.data.patient.birthdate ?? '01-01-1990',
    set: dietStore.updatePatientBirthdate,
  })
  const patientGenderMale = bind(
    () => diet.value?.data.patient.gender === 'male',
    () => {
      dietStore.updatePatientGender('male')
    },
  )
  const patientGenderFemale = bind(
    () => diet.value?.data.patient.gender !== 'male',
    () => {
      dietStore.updatePatientGender('female')
    },
  )
  const patientWeight = bind(
    () => diet.value?.data.patient.weight ?? 58,
    dietStore.updatePatientWeight,
  )
  const patientHeight = bind(
    () => diet.value?.data.patient.height ?? 164,
    dietStore.updatePatientHeight,
  )
  const patientPregnancy = bind(
    () => diet.value?.data.patient.pregnancy.active ?? false,
    dietStore.updatePatientPregnancyActive,
  )
  const patientPregnancyTrimesterOne = bind(
    () => diet.value?.data.patient.pregnancy.trimester === 'one',
    () => {
      dietStore.updatePatientPregnancyTrimester('one')
    },
  )
  const patientPregnancyTrimesterTwo = bind(
    () => diet.value?.data.patient.pregnancy.trimester === 'two',
    () => {
      dietStore.updatePatientPregnancyTrimester('two')
    },
  )
  const patientPregnancyTrimesterThree = bind(
    () => diet.value?.data.patient.pregnancy.trimester === 'three',
    () => {
      dietStore.updatePatientPregnancyTrimester('three')
    },
  )
  const patientLactation = bind(
    () => diet.value?.data.patient.lactation.active ?? false,
    dietStore.updatePatientLactationActive,
  )
  const patientLactationPeriodZeroSix = bind(
    () => diet.value?.data.patient.lactation.period === 'zero_six_months',
    () => {
      dietStore.updatePatientLactationPeriod('zero_six_months')
    },
  )
  const patientLactationPeriodAboveSix = bind(
    () => diet.value?.data.patient.lactation.period === 'above_six_months',
    () => {
      dietStore.updatePatientLactationPeriod('above_six_months')
    },
  )
  const patientActivityLevel = bind(
    () => diet.value?.data.patient.activity_level ?? 1.4,
    dietStore.updatePatientActivityLevel,
  )
  const dateOffset = (years: number, yesterday = false) => {
    const date = new Date()
    if (yesterday) date.setDate(date.getDate() - 1)
    else {
      date.setMonth(0)
      date.setDate(1)
      date.setFullYear(date.getFullYear() - years)
    }
    return date
  }
  const patientBindings = computed(() => ({
    activityLevel: patientActivityLevel.value,
    birthdate: patientBirthdate.value,
    female: patientGenderFemale.value,
    height: patientHeight.value,
    lactation: patientLactation.value,
    lactationAboveSix: patientLactationPeriodAboveSix.value,
    lactationZeroSix: patientLactationPeriodZeroSix.value,
    male: patientGenderMale.value,
    maxDate: dateOffset(0, true),
    minDate: dateOffset(115),
    pregnancy: patientPregnancy.value,
    pregnancyTrimesterOne: patientPregnancyTrimesterOne.value,
    pregnancyTrimesterThree: patientPregnancyTrimesterThree.value,
    pregnancyTrimesterTwo: patientPregnancyTrimesterTwo.value,
    weight: patientWeight.value,
  }))
  const patientListeners = {
    'update:activityLevel': (value: number) => {
      patientActivityLevel.value = value
    },
    'update:birthdate': (value: Date) => {
      patientBirthdate.value = value
    },
    'update:female': () => {
      patientGenderFemale.value = true
    },
    'update:height': (value: number) => {
      patientHeight.value = value
    },
    'update:lactation': (value: boolean) => {
      patientLactation.value = value
    },
    'update:lactationAboveSix': () => {
      patientLactationPeriodAboveSix.value = true
    },
    'update:lactationZeroSix': () => {
      patientLactationPeriodZeroSix.value = true
    },
    'update:male': () => {
      patientGenderMale.value = true
    },
    'update:pregnancy': (value: boolean) => {
      patientPregnancy.value = value
    },
    'update:pregnancyTrimesterOne': () => {
      patientPregnancyTrimesterOne.value = true
    },
    'update:pregnancyTrimesterThree': () => {
      patientPregnancyTrimesterThree.value = true
    },
    'update:pregnancyTrimesterTwo': () => {
      patientPregnancyTrimesterTwo.value = true
    },
    'update:weight': (value: number) => {
      patientWeight.value = value
    },
  }
  const calculatedNorm = (key: Nutrients): number | 'b.d.' => {
    const value = diet.value?.data.norms[key]
    return G.isObject(value) && 'calculated' in value ? (value.calculated ?? 'b.d.') : 'b.d.'
  }
  const inputedNorm = (key: Nutrients): number => {
    const value = diet.value?.data.norms[key]
    return G.isObject(value) && 'calculated' in value ? value.inputed || 0 : 0
  }
  const handleUpdateNormActive = () => {
    if (hasPerm('accounts.diet_norms')) dietStore.updateNormsActive(!diet.value?.data.norms.active)
    else showAppUpgradePlanModal()
  }

  return {
    calculatedNorm,
    getPrecisionForNutrients: (group: string) => (group === 'Kwasy tłuszczowe' ? 3 : 1),
    getStepForNutrients: (group: string) => (group === 'Kwasy tłuszczowe' ? 0.001 : 0.1),
    handleUpdateNorm: (nutrient: Nutrients, value: number) => {
      dietStore.updateNorm({ nutrient, value })
    },
    handleUpdateNormActive,
    hasPerm,
    inputedNorm,
    patientBindings,
    patientListeners,
    showAppUpgradePlanModal,
    updatePatientWeight: dietStore.updatePatientWeight,
  }
}
